/**
 * The portfolio: every account you have, live — what it is worth, how today went, what you hold, the
 * orders that are working and what filled. Flat on the page, sections parted by hairlines; the order
 * ticket is the one panel. A stock opens as candles with your own lines drawn across them.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowLeft, ChartPie, MessagesSquare } from 'lucide-react'
import { scopePortfolio, type PfAccount, type PfHolding, type PfOrder, type PortfolioLive } from '@shared/portfolio'
import type { CompanyProfile, OhlcBar, PortfolioSnapshot, TechnicalAnalysis } from '@shared/types'
import { OrderTicket } from '../components/portfolio/OrderTicket'
import { usePortfolioLive } from '../hooks/usePortfolioLive'
import { api, brokerApi } from './api'
import { INTERVALS, SOURCE, movingAverage, toBars, type Interval } from './bars'
import { Candles, type PriceLine } from './Candles'
import { Chart } from './Chart'
import { CopyPage, CopyRows } from './Copy'
import { Btn, Status, Tabs, cx, pct, toneOf, usd } from './kit'
import { Bar, Empty, Page, ask, useTabs } from './Page'
import { useDemo } from './store'

const money = (n: number | null | undefined) => (n == null ? '—' : usd(n))
const num = (n: number, d = 4) => n.toLocaleString('en-US', { maximumFractionDigits: d })
const delta = (n: number | null | undefined, p?: number | null) => (n == null ? '—' : `${n >= 0 ? '▲' : '▼'} ${usd(Math.abs(n))}${p != null ? ` (${Math.abs(p).toFixed(2)}%)` : ''}`)
const SESSION: Record<string, string> = { pre: 'Pre-market', regular: 'Market open', after: 'After hours', closed: 'Market closed' }
const HUES = ['#7fb2d8', '#bfaef8', '#9fe8c8', '#ffd29a', '#ee82b4', '#ffae74', '#aec2f8', '#b6efcc']
const label = 'm-0 text-[12px] font-normal leading-4 tracking-normal text-ws-label'

/** The live picture, shared by the bar and the page (one stream while the portfolio is on screen). */
function usePortfolio() {
  const live = usePortfolioLive()
  const account = useTabs((s) => s.account)
  const scope = account === 'all' ? null : account
  const shown = useMemo(() => (live.portfolio ? scopePortfolio(live.portfolio, scope) : null), [live.portfolio, scope])
  return { ...live, all: live.portfolio, shown, scope }
}

export function PortfolioBar() {
  const t = useTabs()
  const symbol = useDemo((s) => s.symbol)
  const copyId = useDemo((s) => s.copyId)
  const openStock = useDemo((s) => s.openStock)
  const { all } = usePortfolio()
  if (symbol || copyId)
    return (
      <Bar>
        <Btn icon={ArrowLeft} onClick={() => openStock(null)}>
          Portfolio
        </Btn>
      </Bar>
    )
  const accounts = all?.accounts ?? []
  return (
    <Bar
      right={
        all && (
          <span className="flex items-center gap-4 pr-1 text-[12px] tracking-normal text-ws-fg-3">
            <span className="hidden sm:block">{SESSION[all.session] ?? ''}</span>
            <Status tone={all.live ? 'free' : 'off'}>{all.live ? 'Live' : 'Recorded by hand'}</Status>
          </span>
        )
      }
    >
      <Tabs options={[{ id: 'all', label: accounts.length > 1 ? 'All accounts' : 'Portfolio' }, ...(accounts.length > 1 ? accounts.map((a) => ({ id: a.id, label: `${a.name}${a.mask ? ` ··${a.mask.slice(-4)}` : ''}` })) : [])]} value={accounts.some((a) => a.id === t.account) ? t.account : 'all'} onChange={(v) => t.set({ account: v })} />
    </Bar>
  )
}

const RANGES = ['1D', '1W', '1M', '3M', 'ALL'] as const
type Range = (typeof RANGES)[number]

/** The big number, and the line under it: walk the line and the number follows. */
function Hero({ p, scope }: { p: PortfolioLive; scope: string | null }) {
  const [range, setRange] = useState<Range>('1M')
  const [snaps, setSnaps] = useState<PortfolioSnapshot[]>([])
  const [at, setAt] = useState<number | null>(null)
  useEffect(() => {
    let on = true
    api.portfolioHistory(range, scope ?? 'all').then((s) => on && setSnaps(s), () => on && setSnaps([]))
    return () => {
      on = false
    }
  }, [range, scope])
  // The line ends at what the portfolio is worth right now.
  const points = useMemo(() => [...snaps.map((s) => s.value), p.totals.value], [snaps, p.totals.value])
  const labels = useMemo(() => [...snaps.map((s) => new Date(s.ts).toLocaleString('en-US', range === '1D' ? { hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric', hour: 'numeric' })), 'Now'], [snaps, range])
  const shown = at != null && points[at] != null ? points[at] : p.totals.value
  const since = points.length > 1 ? shown - points[0] : 0
  const sincePct = points.length > 1 && points[0] ? (since / points[0]) * 100 : 0
  const today = at == null
  return (
    <section>
      <h2 className={label}>{at != null ? labels[at] : 'Portfolio value'}</h2>
      <div className="mt-1 text-[40px] font-medium leading-[48px] tracking-[-0.02em] text-ws-fg tabular-nums">{usd(shown)}</div>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] tracking-normal">
        {today ? (
          <>
            <span className={toneOf(p.totals.dayPnl)}>
              {delta(p.totals.dayPnl, p.totals.dayPct)} <span className="text-ws-fg-3">today</span>
            </span>
            <span className={toneOf(p.totals.openPnl)}>
              {delta(p.totals.openPnl, p.totals.openPnlPct)} <span className="text-ws-fg-3">open</span>
            </span>
          </>
        ) : (
          <span className={toneOf(since)}>
            {delta(since, sincePct)} <span className="text-ws-fg-3">since the start of this stretch</span>
          </span>
        )}
      </div>
      {points.length > 2 ? <div className="mt-3">{<Chart points={points} labels={labels} height={220} bare live onScrub={setAt} />}</div> : <div className="mt-3 flex h-[120px] items-center text-[12px] tracking-normal text-ws-fg-4">The line appears once there is some history to draw.</div>}
      <div className="mt-1 flex h-9 items-center gap-1">
        {RANGES.map((r) => (
          <button key={r} type="button" aria-pressed={range === r} onClick={() => setRange(r)} className={cx('h-7 rounded-[6px] px-[10px] text-[12px] font-medium tracking-normal transition-colors duration-150', range === r ? (since >= 0 ? 'text-ws-up' : 'text-ws-down') : 'text-ws-fg-3 hover:text-ws-fg')}>
            {r}
          </button>
        ))}
      </div>
    </section>
  )
}

function Accounts({ accounts }: { accounts: PfAccount[] }) {
  return (
    <section className="grid grid-cols-1 divide-y divide-ws-line border-y border-ws-line sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-3">
      {accounts.map((a) => (
        <div key={a.id} className="min-w-0 px-0 py-4 sm:px-5 sm:first:pl-0">
          <div className="flex items-center gap-2 text-[12px] tracking-normal text-ws-fg-3">
            <span className="truncate text-ws-fg-2">{a.name}</span>
            {a.mask && <span>··{a.mask.slice(-4)}</span>}
            <span className="ml-auto shrink-0">{a.live ? (a.tradable ? <Status tone="free">Trading</Status> : <Status tone="off">Read only</Status>) : <Status tone="off">By hand</Status>}</span>
          </div>
          <div className="mt-1 text-[20px] font-medium leading-7 tracking-[-0.01em] text-ws-fg tabular-nums">{usd(a.value)}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 text-[12px] tracking-normal text-ws-fg-3">
            {a.cash != null && <span>Cash {usd(a.cash)}</span>}
            {a.buyingPower != null && <span>Buying power {usd(a.buyingPower)}</span>}
            {a.dayPnl != null && <span className={toneOf(a.dayPnl)}>{delta(a.dayPnl)} today</span>}
          </div>
          {a.blocked && <div className="mt-1 text-[12px] tracking-normal text-ws-warn">{a.blocked}</div>}
        </div>
      ))}
    </section>
  )
}

/** Where the money is: one bar, a slice for each holding and one for cash. */
function Allocation({ p, onPick }: { p: PortfolioLive; onPick: (code: string) => void }) {
  const total = p.totals.value || 1
  const slices = [...p.holdings.filter((h) => (h.value ?? 0) > 0).map((h, i) => ({ key: h.code, name: h.symbol, value: h.value ?? 0, hue: HUES[i % HUES.length] })), ...(p.totals.cash > 0 ? [{ key: '', name: 'Cash', value: p.totals.cash, hue: '#3a3a3a' }] : [])]
  if (slices.length < 2) return null
  return (
    <section>
      <h2 className={label}>Where it is</h2>
      <div className="mt-3 flex h-2 overflow-hidden rounded-[3px] bg-ws-edge">
        {slices.map((s) => (
          <button key={s.name} type="button" aria-label={s.name} disabled={!s.key} onClick={() => s.key && onPick(s.key)} style={{ width: `${(s.value / total) * 100}%`, background: s.hue }} className="h-full min-w-[2px] border-r border-ws-bg last:border-0" />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12px] tracking-normal text-ws-fg-3">
        {slices.map((s) => (
          <span key={s.name} className="flex items-center gap-[6px]">
            <span className="block h-2 w-2 rounded-[2px]" style={{ background: s.hue }} />
            <span className="text-ws-fg-2">{s.name}</span>
            {((s.value / total) * 100).toFixed(1)}%
          </span>
        ))}
      </div>
    </section>
  )
}

const th = 'h-8 px-2 text-right text-[12px] font-normal tracking-normal text-ws-fg-4 first:pl-0 first:text-left last:pr-0'
const td = 'h-11 px-2 text-right text-[13px] tracking-normal tabular-nums first:pl-0 first:text-left last:pr-0'

function Holdings({ p, onPick }: { p: PortfolioLive; onPick: (code: string) => void }) {
  if (!p.holdings.length) return <p className="m-0 py-6 text-[12px] tracking-normal text-ws-fg-3">Nothing is held here yet.</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[620px] border-collapse">
        <thead>
          <tr className="border-b border-ws-line">
            <th className={th}>Stock</th>
            <th className={th}>Shares</th>
            <th className={th}>Average</th>
            <th className={th}>Price</th>
            <th className={th}>Today</th>
            <th className={th}>Value</th>
            <th className={th}>Gain</th>
          </tr>
        </thead>
        <tbody>
          {p.holdings.map((h) => {
            const workers = h.parts.flatMap((x) => x.workers)
            return (
              <tr key={h.code} onClick={() => onPick(h.code)} className="cursor-pointer border-b border-ws-hair transition-colors duration-150 hover:bg-ws-hover">
                <td className={td}>
                  <span className="font-medium text-ws-fg">{h.symbol}</span>
                  {workers.length > 0 && <span className="ml-2 text-[12px] text-ws-fg-3">{workers.length === 1 ? 'a worker' : `${workers.length} workers`}</span>}
                </td>
                <td className={cx(td, 'text-ws-fg-2')}>{num(h.quantity)}</td>
                <td className={cx(td, 'text-ws-fg-2')}>{money(h.avgCost)}</td>
                <td className={cx(td, 'text-ws-fg')}>{money(h.last)}</td>
                <td className={cx(td, toneOf(h.dayPct ?? 0))}>{h.dayPct == null ? '—' : pct(h.dayPct)}</td>
                <td className={cx(td, 'text-ws-fg')}>{money(h.value)}</td>
                <td className={cx(td, toneOf(h.openPnl ?? 0))}>
                  {h.openPnl == null ? '—' : `${h.openPnl >= 0 ? '+' : '−'}${usd(Math.abs(h.openPnl))}`}
                  {h.openPnlPct != null && <span className="ml-2 text-[12px]">{pct(h.openPnlPct)}</span>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const orderWords = (o: PfOrder) => `${o.type}${o.limit != null ? ` ${usd(o.limit)}` : ''}${o.stop != null ? ` stop ${usd(o.stop)}` : ''}${o.tif ? ` · ${o.tif}` : ''}`

function Orders({ p, onChanged }: { p: PortfolioLive; onChanged: () => void }) {
  const say = useDemo((s) => s.say)
  const names = new Map(p.accounts.map((a) => [a.id, a.name]))
  if (!p.orders.length) return <p className="m-0 py-6 text-[12px] tracking-normal text-ws-fg-3">No order is working.</p>
  return (
    <div className="divide-y divide-ws-hair">
      {p.orders.map((o) => (
        <div key={o.id} className="flex min-h-11 items-center gap-3 py-2 text-[13px] tracking-normal">
          <span className={cx('w-10 shrink-0 font-medium', o.side === 'BUY' ? 'text-ws-up' : 'text-ws-down')}>{o.side === 'BUY' ? 'Buy' : 'Sell'}</span>
          <span className="min-w-0 flex-1">
            <span className="text-ws-fg">
              {o.quantity != null ? num(o.quantity) : ''} {o.symbol}
            </span>
            <span className="ml-2 text-ws-fg-3">{orderWords(o)}</span>
            <span className="block text-[12px] text-ws-fg-4">
              {names.get(o.account) ?? o.account} · {o.status}
              {o.filled ? ` · ${num(o.filled)} filled` : ''}
              {o.workerId ? ' · a worker’s' : o.ticketId ? (o.fromChat ? ' · from a chat' : ' · from the ticket') : o.orderId == null ? ' · placed at the broker' : ''}
            </span>
          </span>
          {o.workerId ? (
            <button type="button" onClick={() => useDemo.setState({ view: 'workers', workerId: o.workerId })} className="shrink-0 rounded-[6px] px-2 py-1 text-[12px] text-ws-fg-3 transition-colors hover:bg-ws-hover hover:text-ws-fg">
              Open worker
            </button>
          ) : (
            o.orderId != null && (
              <button type="button" onClick={() => void brokerApi.cancelOrder(o.orderId!).then(() => (say('Cancel sent'), onChanged()), (e) => say(e instanceof Error ? e.message : 'It could not be cancelled'))} className="shrink-0 rounded-[6px] px-2 py-1 text-[12px] text-ws-fg-3 transition-colors hover:bg-ws-hover hover:text-ws-down">
                Cancel
              </button>
            )
          )}
        </div>
      ))}
    </div>
  )
}

const SOURCE_WORDS: Record<string, string> = { worker: 'a worker', order: 'a chat order', app: 'the ticket', broker: 'at the broker', recorded: 'recorded by hand' }
function Activity({ p }: { p: PortfolioLive }) {
  if (!p.fills.length) return <p className="m-0 py-6 text-[12px] tracking-normal text-ws-fg-3">Nothing has filled yet.</p>
  return (
    <div className="divide-y divide-ws-hair">
      {p.fills.slice(0, 80).map((f) => (
        <div key={f.id} className="flex min-h-10 items-center gap-3 py-2 text-[13px] tracking-normal">
          <span className={cx('w-10 shrink-0 font-medium', f.side === 'BUY' ? 'text-ws-up' : 'text-ws-down')}>{f.side === 'BUY' ? 'Buy' : 'Sell'}</span>
          <span className="min-w-0 flex-1 text-ws-fg">
            {num(f.shares)} {f.symbol} <span className="text-ws-fg-3">at {usd(f.price)}</span>
          </span>
          <span className="shrink-0 text-right text-[12px] text-ws-fg-4">
            {SOURCE_WORDS[f.source] ?? f.source} · {new Date(f.at).toLocaleString('en-US', f.today ? { hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric' })}
          </span>
        </div>
      ))}
    </div>
  )
}

/** The order ticket: the real one, on the right, always in reach. */
function Rail({ all, scope, symbol, onSymbol, onChanged }: { all: PortfolioLive; scope: string | null; symbol: string; onSymbol: (s: string) => void; onChanged: () => void }) {
  return (
    <aside className="lb-legacy lb-embed w-full shrink-0 self-start xl:sticky xl:top-6 xl:w-[360px]">
      <OrderTicket symbol={symbol} onSymbolChange={onSymbol} portfolio={all} scope={scope} onChanged={onChanged} />
    </aside>
  )
}

function Overview({ all, shown, scope, refresh }: { all: PortfolioLive; shown: PortfolioLive; scope: string | null; refresh: () => void }) {
  const openStock = useDemo((s) => s.openStock)
  const [tab, setTab] = useState<'holdings' | 'orders' | 'activity'>('holdings')
  const [picked, setPicked] = useState('')
  const pick = (code: string) => {
    // A holding of an account that cannot be traded from here opens its chart; it is never loaded into the ticket.
    const h = shown.holdings.find((x) => x.code === code)
    const tradable = h?.parts.some((x) => all.accounts.find((a) => a.id === x.account)?.tradable !== false)
    if (tradable) setPicked(code)
    openStock(code)
  }
  return (
    <Page>
      <div className="flex flex-col gap-8 xl:flex-row xl:gap-10">
        <div className="min-w-0 flex-1 space-y-8">
          <Hero p={shown} scope={scope} />
          <Accounts accounts={shown.accounts} />
          <Allocation p={shown} onPick={pick} />
          <CopyRows />
          <section>
            <Tabs
              options={[
                { id: 'holdings', label: 'Holdings', count: shown.holdings.length },
                { id: 'orders', label: 'Orders', count: shown.orders.length || undefined },
                { id: 'activity', label: 'Activity' },
              ]}
              value={tab}
              onChange={setTab}
            />
            <div className="mt-2">{tab === 'holdings' ? <Holdings p={shown} onPick={pick} /> : tab === 'orders' ? <Orders p={shown} onChanged={refresh} /> : <Activity p={shown} />}</div>
          </section>
        </div>
        <Rail all={all} scope={scope} symbol={picked} onSymbol={setPicked} onChanged={refresh} />
      </div>
    </Page>
  )
}

/* ── one stock ───────────────────────────────────────────────────────── */

const CALL: Record<string, string> = { 'strong-buy': 'Strong buy', buy: 'Buy', neutral: 'Neutral', sell: 'Sell', 'strong-sell': 'Strong sell' }
const callTone = (c: string) => (c.includes('buy') ? 'text-ws-up' : c.includes('sell') ? 'text-ws-down' : 'text-ws-fg-2')
const big = (n: number | null) => (n == null ? '—' : n >= 1e12 ? `$${(n / 1e12).toFixed(2)}T` : n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : usd(n))
const Fact = ({ k, children }: { k: string; children: ReactNode }) => (
  <div className="flex min-h-9 items-center justify-between gap-3 text-[13px] tracking-normal">
    <dt className="shrink-0 text-ws-fg-3">{k}</dt>
    <dd className="m-0 truncate text-right text-ws-fg-2 tabular-nums">{children}</dd>
  </div>
)

function Stock({ code, all, scope, refresh }: { code: string; all: PortfolioLive; scope: string | null; refresh: () => void }) {
  const symbol = code.split('.')[0]
  const h = all.holdings.find((x) => x.code === code || x.symbol === symbol)
  const [interval, setIv] = useState<Interval>('1D')
  const [layers, setLayers] = useState({ volume: true, rsi: false, ma20: false, ma50: true, ma200: false })
  const [raw, setRaw] = useState<OhlcBar[] | null>(null)
  const [ta, setTa] = useState<TechnicalAnalysis | null>(null)
  const [co, setCo] = useState<CompanyProfile | null>(null)
  const [quote, setQuote] = useState<{ price: number; change: number | null; changePct: number | null } | null>(null)
  useEffect(() => {
    let on = true
    setRaw(null)
    api.ohlc(code, SOURCE[interval].timeframe as never).then((b) => on && setRaw(b), () => on && setRaw([]))
    return () => {
      on = false
    }
  }, [code, interval])
  useEffect(() => {
    let on = true
    setTa(null)
    setCo(null)
    void api.technicals(code).then((x) => on && setTa(x), () => {})
    void api.profile(code).then((x) => on && setCo(x), () => {})
    void api.quote(code).then((q) => on && setQuote({ price: (q as { price?: number; close?: number }).price ?? (q as { close?: number }).close ?? 0, change: (q as { change?: number }).change ?? null, changePct: (q as { changePercent?: number; changePct?: number }).changePercent ?? (q as { changePct?: number }).changePct ?? null }), () => {})
    return () => {
      on = false
    }
  }, [code])
  const bars = useMemo(() => (raw && raw.length ? toBars(raw, SOURCE[interval].group) : null), [raw, interval])
  const last = h?.last ?? quote?.price ?? bars?.c[bars.c.length - 1] ?? null
  const dayPct = h?.dayPct ?? quote?.changePct ?? null
  // Your own lines: where you stand, the notes you keep, the orders resting at the broker.
  const lines: PriceLine[] = []
  if (h?.avgCost) lines.push({ y: h.avgCost, tone: 'pos', chip: `${num(h.quantity)} sh · avg ${usd(h.avgCost)}`, more: h.openPnl != null ? `${h.openPnl >= 0 ? '+' : '−'}${usd(Math.abs(h.openPnl))}${h.openPnlPct != null ? ` (${pct(h.openPnlPct)})` : ''}` : undefined })
  for (const o of all.orders.filter((x) => x.symbol === symbol)) {
    if (o.stop != null) lines.push({ y: o.stop, tone: o.side === 'SELL' ? 'down' : 'pos', chip: `${o.side === 'SELL' ? 'Stop' : 'Buy stop'} ${usd(o.stop)}`, more: `${o.quantity != null ? num(o.quantity) : ''} sh · ${o.status}` })
    else if (o.limit != null) lines.push({ y: o.limit, tone: o.side === 'SELL' ? 'up' : 'pos', chip: `${o.side === 'SELL' ? 'Sell' : 'Buy'} limit ${usd(o.limit)}`, more: `${o.quantity != null ? num(o.quantity) : ''} sh · ${o.status}` })
  }
  if (h?.stopLoss && !lines.some((l) => Math.abs(l.y - h.stopLoss!) < 0.005)) lines.push({ y: h.stopLoss, tone: 'down', chip: `Stop note ${usd(h.stopLoss)}` })
  if (h?.takeProfit && !lines.some((l) => Math.abs(l.y - h.takeProfit!) < 0.005)) lines.push({ y: h.takeProfit, tone: 'up', chip: `Target note ${usd(h.takeProfit)}` })
  const overlays = bars ? [...(layers.ma20 ? [{ points: movingAverage(bars.c, 20), label: 'MA 20' }] : []), ...(layers.ma50 ? [{ points: movingAverage(bars.c, 50), label: 'MA 50' }] : []), ...(layers.ma200 ? [{ points: movingAverage(bars.c, 200), label: 'MA 200' }] : [])] : []
  const layer = (k: keyof typeof layers, text: string) => (
    <button key={k} type="button" aria-pressed={layers[k]} onClick={() => setLayers({ ...layers, [k]: !layers[k] })} className={cx('h-7 rounded-[6px] px-2 text-[12px] tracking-normal transition-colors duration-150', layers[k] ? 'bg-ws-panel text-ws-fg' : 'text-ws-fg-3 hover:text-ws-fg')}>
      {text}
    </button>
  )
  return (
    <Page>
      <div className="flex flex-col gap-8 xl:flex-row xl:gap-10">
        <div className="min-w-0 flex-1 space-y-7">
          <section>
            <div className="flex items-baseline gap-3">
              <h1 className="m-0 text-[20px] font-medium leading-7 tracking-[-0.01em] text-ws-fg">{symbol}</h1>
              <span className="truncate text-[13px] tracking-normal text-ws-fg-3">{co?.name ?? ''}</span>
              <button type="button" onClick={() => ask(null, `$${symbol} `)} className="ml-auto flex shrink-0 items-center gap-2 rounded-[8px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors hover:bg-ws-hover hover:text-ws-fg">
                <MessagesSquare size={14} strokeWidth={1.8} />
                Ask about it
              </button>
            </div>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="text-[32px] font-medium leading-10 tracking-[-0.02em] text-ws-fg tabular-nums">{last != null ? usd(last) : '—'}</span>
              {dayPct != null && <span className={cx('text-[13px] tracking-normal', toneOf(dayPct))}>{pct(dayPct)} today</span>}
            </div>
          </section>

          <section>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="flex items-center gap-px">
                {INTERVALS.map((i) => (
                  <button key={i} type="button" aria-pressed={interval === i} onClick={() => setIv(i)} className={cx('h-7 rounded-[6px] px-2 text-[12px] font-medium tracking-normal transition-colors duration-150', interval === i ? 'bg-ws-panel text-ws-fg' : 'text-ws-fg-3 hover:text-ws-fg')}>
                    {i}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-px xl:ml-auto">
                {layer('volume', 'Volume')}
                {layer('rsi', 'RSI')}
                {layer('ma20', 'MA 20')}
                {layer('ma50', 'MA 50')}
                {layer('ma200', 'MA 200')}
              </div>
            </div>
            <div className="mt-3">{bars ? <Candles bars={bars} height={440} lines={lines} overlays={overlays} volume={layers.volume} rsi={layers.rsi} /> : <div className="flex h-[440px] items-center justify-center text-[12px] tracking-normal text-ws-fg-4">{raw == null ? 'Loading the candles…' : 'No candles for this stretch.'}</div>}</div>
          </section>

          {ta && (
            <section>
              <h2 className={label}>
                What the indicators say <span className="text-ws-fg-4">· daily, as of {ta.asOf}</span>
              </h2>
              <div className="mt-3 grid grid-cols-1 divide-y divide-ws-line border-y border-ws-line sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                {(
                  [
                    ['Summary', ta.overall, null],
                    ['Oscillators', ta.oscillators.summary, ta.oscillators],
                    ['Moving averages', ta.movingAverages.summary, ta.movingAverages],
                  ] as const
                ).map(([name, call, bucket]) => (
                  <div key={name} className="py-3 sm:px-5 sm:first:pl-0">
                    <div className="text-[12px] tracking-normal text-ws-fg-3">{name}</div>
                    <div className={cx('mt-1 text-[16px] font-medium leading-6', callTone(call))}>{CALL[call] ?? call}</div>
                    {bucket && (
                      <div className="mt-1 text-[12px] tracking-normal text-ws-fg-4">
                        {bucket.buy} buy · {bucket.neutral} neutral · {bucket.sell} sell
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {ta.commentary.length > 0 && <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">{ta.commentary.join(' · ')}</p>}
            </section>
          )}

          {co && (
            <section>
              <h2 className={label}>The company</h2>
              <dl className="m-0 mt-2 grid grid-cols-1 gap-x-10 divide-y divide-ws-hair sm:grid-cols-2 sm:divide-y-0">
                <Fact k="Sector">{co.sector ?? '—'}</Fact>
                <Fact k="Industry">{co.industry ?? '—'}</Fact>
                <Fact k="Market value">{big(co.marketCap)}</Fact>
                <Fact k="Price / earnings">{co.peRatio?.toFixed(1) ?? '—'}</Fact>
                <Fact k="Earnings per share">{co.eps != null ? usd(co.eps) : '—'}</Fact>
                <Fact k="Dividend yield">{co.dividendYield != null ? `${(co.dividendYield * (co.dividendYield < 1 ? 100 : 1)).toFixed(2)}%` : '—'}</Fact>
                <Fact k="52-week range">{co.week52Low != null && co.week52High != null ? `${usd(co.week52Low)} – ${usd(co.week52High)}` : '—'}</Fact>
                <Fact k="Beta">{co.beta?.toFixed(2) ?? '—'}</Fact>
              </dl>
              {co.description && <p className="m-0 mt-3 line-clamp-4 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">{co.description}</p>}
            </section>
          )}
        </div>

        <div className="w-full shrink-0 space-y-6 xl:w-[360px]">
          {h && (
            <section>
              <h2 className={label}>Your position</h2>
              <dl className="m-0 mt-2 divide-y divide-ws-hair border-y border-ws-line">
                <Fact k="Shares">{num(h.quantity)}</Fact>
                <Fact k="Average cost">{money(h.avgCost)}</Fact>
                <Fact k="Value">{money(h.value)}</Fact>
                <Fact k="Gain">
                  <span className={toneOf(h.openPnl ?? 0)}>
                    {h.openPnl == null ? '—' : `${h.openPnl >= 0 ? '+' : '−'}${usd(Math.abs(h.openPnl))}`} {h.openPnlPct != null ? `(${pct(h.openPnlPct)})` : ''}
                  </span>
                </Fact>
                {h.parts.flatMap((x) => x.workers).map((w) => (
                  <Fact key={w.id} k="Worker">
                    <button type="button" onClick={() => useDemo.setState({ view: 'workers', workerId: w.id })} className="text-ws-fg-2 underline decoration-ws-rule underline-offset-2 hover:text-ws-fg">
                      {w.name}
                    </button>
                  </Fact>
                ))}
              </dl>
            </section>
          )}
          <Rail all={all} scope={scope} symbol={code} onSymbol={(s) => s && useDemo.getState().openStock(s)} onChanged={refresh} />
        </div>
      </div>
    </Page>
  )
}

export function PortfolioView() {
  const symbol = useDemo((s) => s.symbol)
  const copyId = useDemo((s) => s.copyId)
  const brokers = useDemo((s) => s.brokers)
  const go = useDemo((s) => s.go)
  const { all, shown, scope, refresh } = usePortfolio()
  if (!brokers)
    return (
      <Empty icon={<ChartPie size={26} strokeWidth={1.6} className="text-ws-icon" />} title="This workspace has no portfolio">
        It is kept apart from your broker accounts: its agents work without one. Your portfolio is in your first workspace.
      </Empty>
    )
  if (copyId) return <CopyPage id={copyId} />
  if (!all || !shown) return <div className="p-10 text-[12px] tracking-normal text-ws-fg-4">Loading the portfolio…</div>
  if (symbol) return <Stock code={symbol} all={all} scope={scope} refresh={refresh} />
  if (!all.accounts.length && !all.holdings.length && !(all.copies?.length))
    return (
      <Empty
        icon={<ChartPie size={26} strokeWidth={1.6} className="text-ws-icon" />}
        title="No account here yet"
        action={
          <Btn variant="white" className="px-3" onClick={() => go('apps')}>
            Connect a broker
          </Btn>
        }
      >
        Connect a broker and this page shows what you hold, live, and lets you trade. Without one, agents still work — research, budgets, plans — and you can keep a record by hand through any agent.
      </Empty>
    )
  return <Overview all={all} shown={shown} scope={scope} refresh={refresh} />
}
